import { money, shortDate } from "./format";

export interface GuildLevelSetting {
  level_number: number;
  display_name: string;
  glory_required: number;
  benefits: string;
  icon_url?: string;
  is_enabled: boolean;
  sort_order: number;
  note: string;
}

export const guildLevelSettings: GuildLevelSetting[] = [
  { level_number: 1, display_name: "聚耀成員", glory_required: 0, benefits: "加入龍來喬公會，開啟基礎活動與會員身份顯示。", icon_url: "/assets/guild-levels/lv1-juyao-member.png", is_enabled: true, sort_order: 1, note: "V1 只讀設定" },
  { level_number: 2, display_name: "銅耀會員", glory_required: 1000, benefits: "累積活躍紀錄，作為後續會員權益參考。", icon_url: "/assets/guild-levels/lv2-tongyao-member.png", is_enabled: true, sort_order: 2, note: "營運可後續調整" },
  { level_number: 3, display_name: "銀耀會員", glory_required: 3000, benefits: "提升公會身份，預留更高階會員活動資格。", icon_url: "/assets/guild-levels/lv3-yinyao-member.png", is_enabled: true, sort_order: 3, note: "營運可後續調整" },
  { level_number: 4, display_name: "金耀會員", glory_required: 8000, benefits: "預留專屬活動、客服關懷與進階權益。", icon_url: "/assets/guild-levels/lv4-jinyao-member.png", is_enabled: true, sort_order: 4, note: "營運可後續調整" },
  { level_number: 5, display_name: "鉑耀會員", glory_required: 15000, benefits: "預留高活躍會員加碼與身份標記。", icon_url: "/assets/guild-levels/lv5-boyao-member.png", is_enabled: true, sort_order: 5, note: "營運可後續調整" },
  { level_number: 6, display_name: "皇耀會員", glory_required: 30000, benefits: "預留高階會員活動、節慶關懷與專屬資格。", icon_url: "/assets/guild-levels/lv6-huangyao-member.png", is_enabled: true, sort_order: 6, note: "營運可後續調整" },
  { level_number: 7, display_name: "鑽耀貴賓", glory_required: 60000, benefits: "預留貴賓活動與人工審核優先參考。", icon_url: "/assets/guild-levels/lv7-zuanyao-vip.png", is_enabled: true, sort_order: 7, note: "營運可後續調整" },
  { level_number: 8, display_name: "星耀貴賓", glory_required: 100000, benefits: "預留星耀級會員專屬權益與進階活動入口。", icon_url: "/assets/guild-levels/lv8-xingyao-vip.png", is_enabled: true, sort_order: 8, note: "營運可後續調整" },
  { level_number: 9, display_name: "尊耀公爵", glory_required: 180000, benefits: "預留高階會員關懷、特別活動與身份展示。", icon_url: "/assets/guild-levels/lv9-zunyao-duke.png", is_enabled: true, sort_order: 9, note: "營運可後續調整" },
  { level_number: 10, display_name: "龍耀公爵", glory_required: 300000, benefits: "預留最高公會身份與頂級權益設定。", icon_url: "/assets/guild-levels/lv10-longyao-duke.png", is_enabled: true, sort_order: 10, note: "營運可後續調整" }
];

export function getGuildLevelInfo(gloryValue?: number | null, settings = guildLevelSettings) {
  const value = Math.max(0, Number(gloryValue ?? 0));
  const enabledLevels = settings.filter((level) => level.is_enabled).sort((a, b) => a.glory_required - b.glory_required);
  const fallback = enabledLevels[0] ?? guildLevelSettings[0];
  const current = enabledLevels.reduce((best, level) => value >= level.glory_required ? level : best, fallback);
  const next = enabledLevels.find((level) => level.glory_required > value) ?? null;
  return { gloryValue: value, current, next, remainingToNext: next ? Math.max(0, next.glory_required - value) : 0, isMaxLevel: !next };
}

export function formatGlory(value?: number | null) {
  return Number(value ?? 0).toLocaleString("zh-TW");
}

export const actionLabelMap: Record<string, string> = {
  bind_member: "會員綁定",
  bind_member_identity_mismatch: "會員綁定身份不符",
  confirm_channel: "確認渠道",
  select_activation_deposit: "指定入盟啟動筆",
  create_test_deposit: "建立測試存款",
  unlock_newbie_step2_local: "開啟第 2 關",
  complete_step2_checkin: "完成第 2 關打卡",
  check_step3_second_deposit: "檢查第 3 關二存",
  grant_step3_gem: "發放第 3 關寶石碎片",
  unlock_invite_branch: "開啟邀友支線",
  complete_step4_quiz: "完成第 4 關問答",
  create_step5_reward: "建立第 5 關三存加碼派發",
  grant_step5_gem: "發放第 5 關寶石碎片",
  create_step6_reward: "建立第 6 關紅包派發",
  issue_newbie_step6_voucher: "發放新人啟航紅包券",
  duplicate_newbie_step6_voucher: "新人啟航紅包券已存在",
  grant_step6_gem: "發放第 6 關寶石碎片",
  create_step7_graduation_rewards: "建立第 7 關畢業派發",
  complete_newbie_graduation: "完成新人畢業",
  issue_invite_code: "建立邀友邀請碼",
  bind_invite_code: "好友綁定邀請碼",
  duplicate_invite_binding: "邀友綁定已存在",
  create_invite_relation: "建立邀友關係",
  duplicate_invite_relation: "邀友關係已存在",
  create_invite_first_deposit_reward: "建立邀友首存獎勵",
  upsert_invite_manual_review: "儲存邀友人工快審",
  create_invite_manual_reward: "建立邀友人工快審獎勵",
  duplicate_invite_manual_reward: "邀友人工快審獎勵已存在",
  create_review_request: "建立審核申請",
  create_rescue_review_by_admin: "後台建立救援金審核申請",
  create_manual_compensation_review: "建立人工補發審核申請",
  create_manual_clawback_review: "建立人工扣回審核申請",
  exchange_gem_for_voucher: "寶石兌換紅包券",
  graduate_week_checkin: "畢業首週打卡",
  graduate_week_single1000_voucher: "畢業首週單筆充值獎勵",
  graduate_week_total5000_voucher: "畢業首週累積充值獎勵",
  draw_newbie_bonus_red_packet: "抽新手加碼紅包",
  draw_graduate_launch_red_packet: "抽畢業啟航紅包",
  draw_advanced_launch_red_packet: "抽高級啟航紅包",
  draw_weekly_bright_red_packet: "抽週耀紅包",
  draw_weekend_bright_red_packet: "抽週末耀旺紅包",
  draw_weekend_advanced_red_packet: "抽高級週末紅包",
  create_regular_deposit_rebate: "建立正式成員常態回饋",
  auto_create_regular_deposit_rebate: "自動建立正式成員常態回饋",
  create_vip_newbie_special: "建立大客新人特批",
  reject_vip_newbie_special: "大客新人特批建立失敗",
  complete_weekly_checkin: "完成週通行證打卡",
  complete_weekly_deposit_1000: "完成週通行證充值任務",
  complete_weekly_red_packet_draw: "完成週通行證紅包抽獎任務",
  complete_weekly_level_view: "完成週通行證會員身份任務",
  complete_weekly_invite_view: "完成週通行證邀友支線任務",
  weekly_pass_reward_2_gem: "週通行證 2 點獎勵",
  weekly_pass_reward_3_voucher: "週通行證 3 點獎勵",
  weekly_pass_reward_5_glory: "週通行證 5 點獎勵",
  weekend_bonus_checkin: "週末耀旺打卡",
  weekend_bonus_single1000_voucher: "週末耀旺單筆充值獎勵",
  weekend_bonus_cumulative5000_voucher: "週末耀旺累積充值獎勵",
  approve_review_request: "通過審核",
  reject_review_request: "拒絕審核",
  cancel_review_request: "取消審核",
  convert_rescue_review_to_reward: "救援金轉入人工派發",
  convert_rescue_review_to_existing_reward: "救援金已存在派發單",
  convert_birthday_review_to_reward: "生日紅包轉入人工派發",
  convert_birthday_review_to_existing_reward: "生日紅包已存在派發單",
  convert_manual_compensation_review_to_reward: "人工補發轉入人工派發",
  convert_manual_compensation_review_to_existing_reward: "人工補發已存在派發單",
  convert_line_checkin_review_to_reward: "LINE 群打卡轉入人工派發",
  convert_line_checkin_review_to_existing_reward: "LINE 群打卡已存在派發單",
  create_gift_helper_review: "建立贈禮小幫手審核",
  convert_gift_helper_review_to_reward: "贈禮小幫手轉入人工派發",
  convert_gift_helper_review_to_existing_reward: "贈禮小幫手已存在派發單",
  update_guild_level_settings: "更新公會等級設定",
  create_reward: "建立派發",
  complete_reward: "完成派發",
  fail_reward: "派發失敗",
  cancel_reward: "取消派發",
  enqueue_reward_crawler_dispatch: "排入贈禮小幫手派發任務",
  duplicate_reward_crawler_dispatch_task: "贈禮小幫手派發任務已存在",
  reward_crawler_dispatch_completed: "贈禮小幫手派發完成",
  reward_crawler_dispatch_failed: "贈禮小幫手派發失敗"
};

export const reviewTypeLabelMap: Record<string, string> = {
  birthday_bonus: "生日紅包",
  rescue_bonus: "救援金 / 回穩補給金",
  line_checkin: "LINE 群打卡",
  telegram_checkin: "Telegram 打卡",
  gift_helper_review: "贈禮小幫手審核",
  manual_compensation: "人工補發",
  manual_clawback: "人工扣回"
};

export const reviewStatusLabelMap: Record<string, string> = {
  pending: "待確認",
  approved: "已通過",
  rejected: "已拒絕",
  canceled: "已取消",
  converted_to_reward: "已轉派發"
};

export const rewardTypeLabelMap: Record<string, string> = {
  newbie_step1_70: "新人第 1 關 70%",
  newbie_step2_10: "新人第 2 關回盟補給",
  newbie_step3_second_deposit_80: "新人第 3 關二存加碼",
  newbie_step4_quiz_10: "新人第 4 關入盟試煉 10%",
  newbie_step5_third_deposit_50: "新人第 5 關三存加碼 50%",
  newbie_step6_red_packet: "新人第 6 關紅包試手氣（legacy）",
  newbie_step6_red_packet_draw: "新人第 6 關新人啟航紅包",
  newbie_bonus_red_packet_draw: "新手加碼紅包",
  graduate_launch_red_packet_draw: "畢業啟航紅包",
  advanced_launch_red_packet_draw: "高級啟航紅包",
  weekly_bright_red_packet_draw: "週耀紅包",
  weekend_bright_red_packet_draw: "週末耀旺紅包",
  weekend_advanced_red_packet_draw: "高級週末紅包",
  regular_deposit_rebate: "正式成員常態回饋",
  newbie_step7_final_10: "新人第 7 關首存最後 10%",
  newbie_step7_graduation_gift: "新人第 7 關畢業禮",
  invite_join_group_6666: "邀友入群獎 6,666",
  invite_first_deposit_50000: "邀友首存獎勵 50,000",
  invite_second_stage_30000: "邀友二存獎 30,000",
  invite_third_deposit_10000: "邀友三存獎勵 10,000",
  birthday_bonus_manual: "生日紅包",
  rescue_bonus_manual: "救援金",
  rescue_fund_monthly: "救援金 / 回穩補給金",
  group_checkin_bonus: "LINE 群打卡獎勵",
  gift_helper_bonus: "贈禮小幫手獎勵",
  manual_compensation: "人工補發",
  manual_clawback: "人工扣回"
};

export const rewardStatusLabelMap: Record<string, string> = {
  ready: "可派發 / 等待派發",
  scheduled: "預約派發",
  processing: "派發中",
  sent: "已送出",
  completed: "已完成",
  failed: "失敗 / 待人工處理",
  canceled: "已取消"
};

export const voucherTypeLabelMap: Record<string, string> = {
  newbie_bonus_red_packet: "新手加碼紅包券",
  newbie_launch_red_packet: "新人啟航紅包券",
  graduate_launch_red_packet: "畢業啟航紅包券",
  advanced_launch_red_packet: "高級啟航紅包券",
  graduate_start: "畢業啟航紅包券（legacy）",
  advanced_start: "高級啟航紅包券（legacy）",
  starter_bonus: "新手加碼紅包券（legacy）",
  weekly_bright_red_packet: "週耀紅包券",
  weekend_bright_red_packet: "週末耀旺紅包券",
  weekend_advanced_red_packet: "高級週末紅包券",
  weekly_glory: "週耀紅包券（legacy）",
  weekend_packet: "週末紅包券（legacy）"
};

export const fieldLabelMap: Record<string, string> = {
  member_account: "會員帳號",
  review_type: "審核類型",
  status: "狀態",
  title: "標題",
  amount: "金額",
  reward_type: "派發類型",
  reward_status: "派發狀態",
  source_activity_code: "來源活動",
  risk_note: "風控備註",
  applicant_note: "申請備註",
  review_note: "審核備註",
  reject_reason: "拒絕原因",
  cancel_reason: "取消原因",
  reviewed_by: "審核人",
  reviewed_at: "審核時間",
  created_by: "建立人",
  created_at: "建立時間",
  updated_at: "更新時間",
  unique_key: "防重複鍵",
  reward_id: "派發 ID",
  deposit_id: "存款 ID",
  deposit_amount: "存款金額",
  cumulative_amount: "累積金額",
  source_deposit_id: "存款 ID",
  inviter_account: "邀請人帳號",
  invitee_account: "好友帳號",
  invite_code: "邀請碼",
  invite_link: "邀請連結",
  invite_relation_id: "邀友關係 ID",
  line_added: "已加 LINE",
  group_joined: "已入群",
  own_channel: "本線渠道",
  first_deposit_1000_confirmed: "首存是否達標",
  first_deposit_amount: "好友首存金額",
  second_stage_confirmed: "二存達成",
  second_deposit_amount: "好友二存金額",
  third_deposit_confirmed: "三存達成",
  third_deposit_amount: "三存金額",
  join_reward_id: "入群獎派發 ID",
  first_deposit_reward_id: "首存獎勵派發 ID",
  second_stage_reward_id: "二存獎派發 ID",
  third_deposit_reward_id: "三存獎勵派發 ID",
  reward_target: "獎勵對象",
  review_id: "快審 ID",
  activation_deposit_id: "入盟啟動筆 ID",
  reward_amount: "派發金額",
  second_deposit_id: "二存 ID",
  step7_completed_at: "第 7 關完成時間",
  step7_final10_reward_id: "第 7 關首存最後 10% 派發 ID",
  step7_graduation_reward_id: "第 7 關畢業禮派發 ID",
  final10_reward_id: "首存最後 10% 派發 ID",
  graduation_gift_reward_id: "畢業禮派發 ID",
  final10_amount: "首存最後 10% 金額",
  graduation_gift_amount: "畢業禮金額",
  glory_added: "增加閃耀值",
  graduation_at: "新人畢業時間",
  previous_status: "原狀態",
  new_status: "新狀態",
  operator: "操作人",
  percent: "比例",
  rebate_percent: "回饋比例",
  manual_confirm: "人工確認",
  manual_confirm_note: "人工確認備註",
  manual_confirm_notes: "人工確認備註",
  manual_confirm_reason: "人工確認原因",
  confirm_ready_only: "確認只建立可派發單",
  rejected_reason: "拒絕原因",
  request_payload: "請求資料",
  payload_json: "原始審核資料",
  raw_json: "原始資料",
  created_reward_ids: "新建立派發 ID",
  existing_reward_ids: "既有派發 ID",
  mode: "模式",
  rule_version: "規則版本",
  note: "備註",
  birthday_month: "生日月份",
  birthday_day: "生日日期",
  year: "年度",
  apply_month: "申請月份",
  reason: "原因",
  suggested_amount: "建議金額",
  special_approval: "特批",
  manual_deposit_checked: "已人工核對存款",
  manual_bet_checked: "已人工核對投注 / 輸贏",
  manual_risk_checked: "已人工核對風控",
  created_from: "建立來源",
  agree_review: "同意審核資料",
  dispatch_method: "派發方式",
  review_request_id: "審核申請 ID",
  related_reward_id: "關聯派發 ID",
  member_notified: "已通知會員",
  exchange_type: "兌換類型",
  gem_cost: "寶石成本",
  voucher_type: "紅包券類型",
  voucher_amount: "紅包券數量",
  voucher_id: "紅包券 ID",
  draw_ref: "抽獎編號",
  prize_amount: "抽中金額",
  prize_name: "獎項名稱",
  exchange_ref: "兌換編號",
  source: "來源",
  line_nickname: "LINE 群暱稱",
  checkin_date: "打卡日期",
  proof_url: "截圖 / 圖片連結",
  proof_text: "打卡證明文字",
  raw_gift_record_id: "贈禮原始紀錄 ID",
  external_id: "外部紀錄 ID",
  gift_type: "贈禮類型",
  raw_amount: "原始金額",
  confirmed_amount: "確認派發金額",
  occurred_at: "發生時間",
  admin_note: "後台備註",
  review_unique_key: "審核防重複鍵",
  reward_unique_key: "派發防重複鍵",
  invite_manual_review_id: "邀友快審 ID",
  source_inviter_account: "來源邀請人",
  source_invitee_account: "來源好友",
  source_invitee_line_nickname: "來源好友 LINE 暱稱",
  source_first_deposit_amount: "來源首存金額",
  source_second_deposit_amount: "來源二存金額",
  source_third_deposit_amount: "來源三存金額",
  task_id: "派發任務 ID",
  dispatch_mode: "派發模式",
  dispatch_status: "派發任務狀態",
  queued_at: "排入時間",
  started_at: "開始時間",
  completed_at: "完成時間",
  failed_at: "失敗時間",
  failed_reason: "失敗原因",
  attempt_count: "嘗試次數",
  crawler_reference_id: "爬蟲回傳 ID"
};

export interface AdminLogLike {
  action?: string;
  target_type?: string;
  target_id?: string | null;
  payload?: string | null;
}

export function actionLabel(action?: string | null) {
  if (!action) return "未知操作";
  return actionLabelMap[action] ?? "未知操作";
}

export function reviewTypeLabel(type?: string | null) {
  if (!type) return "-";
  return reviewTypeLabelMap[type] ?? type;
}

export function reviewStatusLabel(status?: string | null) {
  if (!status) return "-";
  return reviewStatusLabelMap[status] ?? status;
}

export function rewardTypeLabel(type?: string | null) {
  if (!type) return "-";
  return rewardTypeLabelMap[type] ?? type;
}

export function rewardStatusLabel(status?: string | null) {
  if (!status) return "-";
  return rewardStatusLabelMap[status] ?? status;
}

export function voucherTypeLabel(type?: string | null) {
  if (!type) return "-";
  return voucherTypeLabelMap[type] ?? type;
}

export function parseJsonObject(raw?: string | null): Record<string, unknown> | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
  } catch {
    return null;
  }
}

export function prettyRawJson(raw?: unknown) {
  if (typeof raw === "string") {
    const parsed = parseJsonObject(raw);
    return parsed ? JSON.stringify(parsed, null, 2) : raw;
  }
  return JSON.stringify(raw ?? {}, null, 2);
}

export function formatAdminLogSummary(row: AdminLogLike) {
  const payload = parseJsonObject(row.payload);
  const action = row.action ?? "";
  const member = stringValue(payload?.member_account) || stringValue(row.target_id);

  if (action === "complete_newbie_graduation") {
    return `完成新人畢業｜會員 ${member || "-"}${append("原狀態", stringValue(payload?.previous_status))}${append("新狀態", stringValue(payload?.new_status))}${append("畢業時間", shortDate(stringValue(payload?.graduation_at)))}${append("閃耀值", stringValue(payload?.glory_added))}`;
  }
  if (action === "create_step7_graduation_rewards") {
    return `建立第 7 關畢業派發｜會員 ${member || "-"}${append("首存最後 10%", moneyValue(payload?.final10_amount))}${append("畢業禮", moneyValue(payload?.graduation_gift_amount))}${append("首存最後 10% 派發 ID", stringValue(payload?.final10_reward_id))}${append("畢業禮派發 ID", stringValue(payload?.graduation_gift_reward_id))}`;
  }
  if (action === "update_guild_level_settings") {
    const changed = Array.isArray(payload?.changed_level_numbers) ? (payload?.changed_level_numbers as unknown[]).join(", ") : stringValue(payload?.changed_level_numbers);
    return `更新公會等級設定${append("版本", stringValue(payload?.version))}${append("修改等級", changed)}`;
  }
  if (action === "create_rescue_review_by_admin") {
    return `後台建立救援金審核申請｜會員 ${member || "-"}${append("申請月份", stringValue(payload?.apply_month))}${append("建議金額", moneyValue(payload?.suggested_amount ?? payload?.amount))}${append("特批", booleanLabel(payload?.special_approval))}${append("原因", stringValue(payload?.reason))}`;
  }
  if (action === "create_manual_compensation_review") {
    return `建立人工補發審核申請｜會員 ${member || "-"}${append("補發金額", moneyValue(payload?.amount))}${append("補發原因", stringValue(payload?.reason))}${append("關聯派發 ID", stringValue(payload?.related_reward_id))}`;
  }
  if (action === "create_manual_clawback_review") {
    return `建立人工扣回審核申請｜會員 ${member || "-"}${append("扣回金額", moneyValue(payload?.amount))}${append("扣回原因", stringValue(payload?.reason))}${append("已通知會員", booleanLabel(payload?.member_notified))}`;
  }
  if (action === "exchange_gem_for_voucher") {
    return `寶石兌換紅包券｜會員 ${member || "-"}${append("扣除寶石", stringValue(payload?.gem_cost))}${append("取得券種", voucherTypeLabel(stringValue(payload?.voucher_type)))}${append("兌換編號", stringValue(payload?.exchange_ref))}`;
  }
  if (action === "graduate_week_checkin") {
    return `畢業首週打卡｜會員 ${member || "-"}｜寶石碎片 +1${append("編號", stringValue(payload?.unique_key))}`;
  }
  if (action === "graduate_week_single1000_voucher") {
    return `畢業首週單筆充值獎勵｜會員 ${member || "-"}｜畢業啟航紅包券 1 張${append("存款 ID", stringValue(payload?.deposit_id))}${append("存款金額", moneyValue(payload?.deposit_amount))}`;
  }
  if (action === "graduate_week_total5000_voucher") {
    return `畢業首週累積充值獎勵｜會員 ${member || "-"}｜高級啟航紅包券 1 張${append("累積充值", moneyValue(payload?.cumulative_amount))}`;
  }
  if (action === "draw_newbie_bonus_red_packet") {
    return `抽新手加碼紅包｜會員 ${member || "-"}${append("抽中金額", moneyValue(payload?.prize_amount))}${append("派發 ID", stringValue(payload?.reward_id))}${append("券 ID", stringValue(payload?.voucher_id))}`;
  }
  if (action === "draw_graduate_launch_red_packet") {
    return `抽畢業啟航紅包｜會員 ${member || "-"}${append("抽中金額", moneyValue(payload?.prize_amount))}${append("派發 ID", stringValue(payload?.reward_id))}${append("券 ID", stringValue(payload?.voucher_id))}`;
  }
  if (action === "draw_advanced_launch_red_packet") {
    return `抽高級啟航紅包｜會員 ${member || "-"}${append("抽中金額", moneyValue(payload?.prize_amount))}${append("派發 ID", stringValue(payload?.reward_id))}${append("券 ID", stringValue(payload?.voucher_id))}`;
  }
  if (action === "draw_weekly_bright_red_packet") {
    return `抽週耀紅包｜會員 ${member || "-"}${append("抽中金額", moneyValue(payload?.prize_amount))}${append("派發 ID", stringValue(payload?.reward_id))}${append("券 ID", stringValue(payload?.voucher_id))}`;
  }
  if (action === "draw_weekend_bright_red_packet") {
    return `抽週末耀旺紅包｜會員 ${member || "-"}${append("抽中金額", moneyValue(payload?.prize_amount))}${append("派發 ID", stringValue(payload?.reward_id))}${append("券 ID", stringValue(payload?.voucher_id))}`;
  }
  if (action === "draw_weekend_advanced_red_packet") {
    return `抽高級週末紅包｜會員 ${member || "-"}${append("抽中金額", moneyValue(payload?.prize_amount))}${append("派發 ID", stringValue(payload?.reward_id))}${append("券 ID", stringValue(payload?.voucher_id))}`;
  }
  if (action === "weekend_bonus_checkin") {
    return `週末耀旺打卡｜會員 ${member || "-"}${append("週末", stringValue(payload?.weekend_id))}${append("完成時間", stringValue(payload?.completed_at))}`;
  }
  if (action === "weekend_bonus_single1000_voucher") {
    return `週末耀旺單筆充值獎勵｜會員 ${member || "-"}｜週末耀旺紅包券 1 張${append("存款 ID", stringValue(payload?.deposit_id))}${append("存款金額", moneyValue(payload?.deposit_amount))}${append("券 ID", stringValue(payload?.voucher_id))}`;
  }
  if (action === "weekend_bonus_cumulative5000_voucher") {
    return `週末耀旺累積充值獎勵｜會員 ${member || "-"}｜高級週末紅包券 1 張${append("累積充值", moneyValue(payload?.cumulative_amount))}${append("券 ID", stringValue(payload?.voucher_id))}`;
  }
  if (action === "create_regular_deposit_rebate" || action === "auto_create_regular_deposit_rebate") {
    const prefix = action === "auto_create_regular_deposit_rebate" ? "自動建立正式成員常態回饋" : "建立正式成員常態回饋";
    return `${prefix}｜會員 ${member || "-"}${append("存款 ID", stringValue(payload?.deposit_id))}${append("存款金額", moneyValue(payload?.deposit_amount))}${append("回饋比例", percentValue(payload?.rebate_percent))}${append("派發金額", moneyValue(payload?.reward_amount))}${append("派發 ID", stringValue(payload?.reward_id))}${append("人工確認", booleanLabel(payload?.manual_confirm))}${append("人工確認備註", stringValue(payload?.manual_confirm_note ?? payload?.manual_confirm_notes))}${append("人工確認原因", stringValue(payload?.manual_confirm_reason))}`;
  }
  if (action === "create_vip_newbie_special") {
    return `建立大客新人特批｜會員 ${member || "-"}${append("模式", vipNewbieSpecialModeLabel(stringValue(payload?.mode)))}${append("首存 ID", stringValue(payload?.activation_deposit_id))}${append("二存 ID", stringValue(payload?.second_deposit_id))}${append("三存 ID", stringValue(payload?.third_deposit_id))}${append("新建立派發 ID", arrayText(payload?.created_reward_ids))}${append("既有派發 ID", arrayText(payload?.existing_reward_ids))}${append("原因", stringValue(payload?.reason))}`;
  }
  if (action === "reject_vip_newbie_special") {
    return `大客新人特批建立失敗｜會員 ${member || "-"}${append("拒絕原因", stringValue(payload?.rejected_reason))}`;
  }
  if (action === "issue_invite_code") {
    return `建立邀友邀請碼｜會員 ${member || "-"}${append("邀請碼", stringValue(payload?.invite_code))}`;
  }
  if (action === "bind_invite_code" || action === "duplicate_invite_binding") {
    return `${action === "bind_invite_code" ? "好友綁定邀請碼" : "邀友綁定已存在"}｜邀請人 ${stringValue(payload?.inviter_account) || "-"}｜好友 ${stringValue(payload?.invitee_account) || "-"}${append("邀請碼", stringValue(payload?.invite_code))}${append("邀友關係 ID", stringValue(payload?.relation_id))}`;
  }
  if (action === "create_invite_relation" || action === "duplicate_invite_relation") {
    return `${action === "create_invite_relation" ? "建立邀友關係" : "邀友關係已存在"}｜會員 ${member || "-"}${append("好友帳號", stringValue(payload?.invitee_account))}${append("邀友關係 ID", stringValue(payload?.relation_id))}`;
  }
  if (action === "create_invite_first_deposit_reward") {
    return `建立邀友首存獎勵｜邀請人 ${member || "-"}${append("好友帳號", stringValue(payload?.invitee_account))}${append("存款 ID", stringValue(payload?.deposit_id))}${append("派發 ID", stringValue(payload?.reward_id))}${append("金額", moneyValue(payload?.amount))}`;
  }
  if (action === "upsert_invite_manual_review") {
    return `儲存邀友人工快審｜邀請人 ${stringValue(payload?.inviter_account) || "-"}｜好友 ${stringValue(payload?.invitee_account) || "-"}${append("快審 ID", stringValue(payload?.review_id))}${append("好友 LINE 暱稱", stringValue(payload?.line_nickname))}${append("已加 LINE", booleanLabel(payload?.line_added))}${append("已入群", booleanLabel(payload?.group_joined))}${append("本線渠道", booleanLabel(payload?.own_channel))}${append("好友首存金額", moneyValue(payload?.first_deposit_amount))}${append("好友二存金額", moneyValue(payload?.second_deposit_amount))}${append("二存達成", booleanLabel(payload?.second_stage_confirmed))}${append("三存達成", booleanLabel(payload?.third_deposit_confirmed))}${append("三存金額", moneyValue(payload?.third_deposit_amount))}`;
  }
  if (action === "create_invite_manual_reward" || action === "duplicate_invite_manual_reward") {
    return `${action === "create_invite_manual_reward" ? "建立邀友人工快審獎勵" : "邀友人工快審獎勵已存在"}｜邀請人 ${stringValue(payload?.inviter_account) || "-"}｜好友 ${stringValue(payload?.invitee_account) || "-"}${append("快審 ID", stringValue(payload?.review_id))}${append("派發 ID", stringValue(payload?.reward_id))}${append("派發類型", rewardTypeLabel(stringValue(payload?.reward_type)))}${append("獎勵對象", inviteRewardTargetLabel(stringValue(payload?.reward_target)))}`;
  }
  if (action.startsWith("convert_") && action.includes("_to_reward")) {
    return `轉入人工派發｜會員 ${member || "-"}｜${rewardTypeLabel(stringValue(payload?.reward_type))}${append("派發 ID", stringValue(payload?.reward_id))}${append("金額", moneyValue(payload?.amount))}`;
  }
  if (action === "create_review_request") return `建立${reviewTypeLabel(stringValue(payload?.review_type))}審核申請｜會員 ${member || "-"}｜狀態：${reviewStatusLabel(stringValue(payload?.status) || "pending")}`;
  if (action === "approve_review_request") return `通過${reviewTypeLabel(stringValue(payload?.review_type))}審核｜會員 ${member || "-"}${append("備註", stringValue(payload?.review_note))}`;
  if (action === "reject_review_request") return `拒絕${reviewTypeLabel(stringValue(payload?.review_type))}審核｜會員 ${member || "-"}${append("原因", stringValue(payload?.reject_reason))}`;
  if (action === "cancel_review_request") return `取消${reviewTypeLabel(stringValue(payload?.review_type))}審核｜會員 ${member || "-"}${append("原因", stringValue(payload?.cancel_reason))}`;
  if (action === "create_reward") return `建立${rewardTypeLabel(stringValue(payload?.reward_type))}派發｜會員 ${member || "-"}${append("金額", moneyValue(payload?.amount))}`;
  if (action === "complete_reward") return `完成派發｜會員 ${member || "-"}｜狀態：${rewardStatusLabel(stringValue(payload?.reward_status) || "completed")}`;
  if (action === "complete_step4_quiz") return `完成第 4 關問答｜會員 ${member || "-"}${append("派發 ID", stringValue(payload?.reward_id))}${append("金額", moneyValue(payload?.reward_amount))}`;
  if (action === "create_step5_reward") return `建立新人第 5 關三存加碼 50% 派發｜會員 ${member || "-"}${append("派發 ID", stringValue(payload?.reward_id))}${append("金額", moneyValue(payload?.reward_amount))}`;
  if (action === "grant_step5_gem") return `發放第 5 關寶石碎片｜會員 ${member || "-"}${append("派發 ID", stringValue(payload?.reward_id))}${append("數量", stringValue(payload?.amount))}`;
  if (action === "create_step6_reward") return `建立新人第 6 關新人啟航紅包派發｜會員 ${member || "-"}${append("派發 ID", stringValue(payload?.reward_id))}${append("金額", moneyValue(payload?.reward_amount))}${append("draw", stringValue(payload?.step6_draw_id))}`;
  if (action === "issue_newbie_step6_voucher") return `發放新人啟航紅包券｜會員 ${member || "-"}${append("券 ID", stringValue(payload?.voucher_id))}`;
  if (action === "duplicate_newbie_step6_voucher") return `新人啟航紅包券已存在｜會員 ${member || "-"}${append("券 ID", stringValue(payload?.voucher_id))}`;
  if (action === "grant_step6_gem") return `發放第 6 關寶石碎片｜會員 ${member || "-"}${append("派發 ID", stringValue(payload?.reward_id))}${append("數量", stringValue(payload?.amount))}`;
  if (payload) return formatPayloadLines(payload).join("｜") || "未知操作";
  return row.payload || "未知操作";
}

export function formatPayloadLines(payload?: Record<string, unknown> | null) {
  if (!payload) return [];
  return Object.entries(payload).map(([key, value]) => `${fieldLabelMap[key] ?? key}：${formatPayloadValue(key, value)}`);
}

export function formatReviewRequestSummary(row: {
  review_type?: string | null;
  status?: string | null;
  amount?: number | null;
  payload_json?: string | null;
  applicant_note?: string | null;
  review_note?: string | null;
  reject_reason?: string | null;
  cancel_reason?: string | null;
}) {
  const payload = parseJsonObject(row.payload_json);
  const parts: string[] = [];
  if (payload) {
    pushPart(parts, "LINE 群暱稱", stringValue(payload.line_nickname));
    pushPart(parts, "打卡日期", stringValue(payload.checkin_date));
    pushPart(parts, "截圖 / 圖片連結", stringValue(payload.proof_url));
    pushPart(parts, "打卡證明文字", stringValue(payload.proof_text));
    pushPart(parts, "外部紀錄 ID", stringValue(payload.external_id));
    pushPart(parts, "贈禮類型", giftTypeValueLabel(payload.gift_type));
    pushPart(parts, "原始金額", moneyValue(payload.raw_amount));
    pushPart(parts, "確認派發金額", moneyValue(payload.confirmed_amount));
    pushPart(parts, "發生時間", shortDate(stringValue(payload.occurred_at)));
    pushPart(parts, "後台備註", stringValue(payload.admin_note));
    pushPart(parts, "申請月份", stringValue(payload.apply_month));
    pushPart(parts, "生日", birthdayText(payload));
    pushPart(parts, "建議金額", moneyValue(payload.suggested_amount));
    pushPart(parts, "原因", stringValue(payload.reason));
    pushPart(parts, "申請備註", stringValue(payload.applicant_note));
    pushPart(parts, "風控備註", stringValue(payload.risk_note));
    pushPart(parts, "特批", booleanLabel(payload.special_approval));
    pushPart(parts, "已通知會員", booleanLabel(payload.member_notified));
    pushPart(parts, "關聯派發 ID", stringValue(payload.related_reward_id));
    pushPart(parts, "來源活動", stringValue(payload.source_activity_code));
  }
  if (row.amount) pushPart(parts, "金額", moneyValue(row.amount));
  if (row.status === "approved") parts.push("狀態：已通過，尚未轉派發");
  if (row.status === "converted_to_reward") parts.push("派發狀態：已轉入派發中心");
  pushPart(parts, "審核備註", row.review_note);
  pushPart(parts, "拒絕原因", row.reject_reason);
  pushPart(parts, "取消原因", row.cancel_reason);
  pushPart(parts, "申請備註", row.applicant_note);
  return parts.join("｜") || "-";
}

export function formatPayloadValue(key: string, value: unknown) {
  const text = stringValue(value);
  if (key === "review_type") return reviewTypeLabel(text);
  if (key === "status") return reviewStatusLabel(text);
  if (key === "reward_type") return rewardTypeLabel(text);
  if (key === "reward_status") return rewardStatusLabel(text);
  if (key === "voucher_type") return voucherTypeLabel(text);
  if (key === "gift_type") return giftTypeValueLabel(value);
  if (key === "amount" || key === "reward_amount" || key === "suggested_amount") return moneyValue(value);
  if (key.endsWith("_at") || key === "created_at" || key === "updated_at") return shortDate(text);
  if (typeof value === "boolean") return booleanLabel(value);
  return text || "-";
}

export async function copyRawJson(raw: unknown) {
  const text = prettyRawJson(raw);
  await navigator.clipboard.writeText(text);
}

function append(label: string, value?: string | null) {
  return value ? `｜${label}：${value}` : "";
}

function pushPart(parts: string[], label: string, value?: string | null) {
  if (value) parts.push(`${label}：${value}`);
}

function stringValue(value: unknown) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return JSON.stringify(value);
}

function moneyValue(value: unknown) {
  const amount = Number(value ?? 0);
  return Number.isFinite(amount) && amount !== 0 ? money(amount) : "";
}

function percentValue(value: unknown) {
  const percent = Number(value ?? 0);
  return Number.isFinite(percent) && percent > 0 ? `${Math.round(percent * 100)}%` : "";
}

function booleanLabel(value: unknown) {
  if (value === true) return "是";
  if (value === false) return "否";
  return "";
}

function giftTypeValueLabel(value: unknown) {
  const text = stringValue(value);
  if (text === "gift_helper") return "贈禮小幫手";
  if (text === "unsupported_gift_type") return "不支援的贈禮類型";
  return text;
}

function inviteRewardTargetLabel(value: string) {
  if (value === "inviter") return "邀請人";
  if (value === "invitee") return "好友";
  return value;
}

function arrayText(value: unknown) {
  return Array.isArray(value) ? value.join(", ") : stringValue(value);
}

function vipNewbieSpecialModeLabel(value: string) {
  if (value === "activation_only") return "只特批首存";
  if (value === "activation_second") return "特批首存 + 二存";
  if (value === "activation_second_third") return "特批首存 + 二存 + 三存";
  return value;
}

function birthdayText(payload: Record<string, unknown>) {
  const month = stringValue(payload.birthday_month);
  const day = stringValue(payload.birthday_day);
  return month && day ? `${month} 月 ${day} 日` : "";
}
